import {
  Controller,
  ControllerRenderProps,
  UseFormReturn,
  useWatch,
} from 'react-hook-form';

import { ErrorMessage } from '@hookform/error-message';

import { Label } from 'components/Label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from 'components/Select';
import { RHFDebounceSelect } from 'components/input/RHFDebounceSelect';
import { Flex, Grid } from 'components/layout';
import { Typography } from 'components/typography';
import { isCustomerSupportedTMS } from 'hooks/useCustomers';
import { useIsTqlInbox } from 'hooks/useIsTqlInbox';
import { useServiceFeatures } from 'hooks/useServiceContext';
import { IntegrationCore } from 'types/Integration';
import { TransportType } from 'types/QuoteRequest';
import { mapCustomerToSelectOptions } from 'utils/loadInfoAndBuilding';

import { EquipmentInfoTooltip } from '../EquipmentInfoTooltip';
import { useQuickQuoteContext } from '../context/QuickQuoteContext';
import { useHasSuperDispatchQuoting } from '../hooks/useHasSuperDispatchQuoting';
import {
  AUTO_TRANSPORT_OPTION,
  QuickQuoteInputs,
  SubTransportTypeOption,
  TransportTypeOption,
  getParentTransportTypeForSubTransportType,
  getSubTransportTypeSelectOptions,
} from '../types';
import { AutoTransportFields } from './AutoTransport/AutoTransportFields';

interface QuoteInformationSectionProps {
  formMethods: UseFormReturn<QuickQuoteInputs, any, QuickQuoteInputs>;
  transportTypeOptions: TransportTypeOption[];
  subTransportTypeOptions?: SubTransportTypeOption[];
  isSubTransportTypesEnabled?: boolean;
  tmsIntegrations?: IntegrationCore[];
}

export const QuoteInformationSection = ({
  formMethods,
  transportTypeOptions,
  subTransportTypeOptions = [],
  isSubTransportTypesEnabled = false,
  tmsIntegrations,
}: QuoteInformationSectionProps) => {
  const {
    control,
    setValue,
    formState: { errors },
    getValues,
  } = formMethods;

  const {
    customers,
    customersLoading,
    handleRefreshCustomers,
    handleResetCustomerSearch,
    handleCustomerSearch,
  } = useQuickQuoteContext();

  const { quotingIntegrations } = useServiceFeatures();
  const transportType = useWatch({
    control,
    name: 'transportType',
  });
  const hasSuperDispatchQuoting = useHasSuperDispatchQuoting();

  const isTql = useIsTqlInbox();

  const useGranularEquipmentSelect =
    isSubTransportTypesEnabled && subTransportTypeOptions.length > 0;

  const equipmentSelectOptions = [
    ...(hasSuperDispatchQuoting
      ? [{ ...AUTO_TRANSPORT_OPTION, group: 'Auto' }]
      : []),
    ...getSubTransportTypeSelectOptions(subTransportTypeOptions),
  ];

  return (
    <Grid gap='lg' className='w-full min-w-0 max-w-full mx-0'>
      <Typography
        variant='h5'
        className='text-neutral-800 font-semibold whitespace-nowrap'
      >
        Quote Information
      </Typography>

      {tmsIntegrations &&
        tmsIntegrations.length > 0 &&
        isCustomerSupportedTMS(tmsIntegrations) && (
          <div>
            <RHFDebounceSelect
              name='customerName'
              label='Customer'
              control={control}
              errors={errors}
              data={customers}
              isLoading={customersLoading}
              showSearchParamDropdown={false}
              refreshHandler={handleRefreshCustomers}
              resetOptionsHandler={handleResetCustomerSearch}
              fetchOptions={handleCustomerSearch}
              mapOptions={mapCustomerToSelectOptions}
              required={false}
            />
          </div>
        )}

      <div>
        <Flex align='center' gap='xs'>
          <Label
            name={
              useGranularEquipmentSelect ? 'subTransportType' : 'transportType'
            }
            required={true}
          >
            {isTql ? 'Equipment Type' : 'Transport Type'}
          </Label>
          {useGranularEquipmentSelect && (
            <EquipmentInfoTooltip
              quotingIntegrations={quotingIntegrations}
              tmsIntegrations={tmsIntegrations}
            />
          )}
        </Flex>
        {useGranularEquipmentSelect ? (
          <Controller
            name='subTransportType'
            control={control}
            rules={{
              validate: (value) => {
                if (getValues('transportType') === TransportType.AUTO) {
                  return true;
                }

                if (!value) {
                  return 'Required';
                }

                return true;
              },
            }}
            render={({ field }: { field: ControllerRenderProps<any> }) => (
              <Select
                options={equipmentSelectOptions}
                showSearch
                onChange={(value) => {
                  const selectedValue = String(value);
                  if (selectedValue === TransportType.AUTO) {
                    setValue('transportType', TransportType.AUTO, {
                      shouldValidate: true,
                    });
                    setValue('subTransportType', '', {
                      shouldValidate: true,
                    });
                    return;
                  }

                  field.onChange(selectedValue);
                  const parentTransportType =
                    getParentTransportTypeForSubTransportType(
                      selectedValue,
                      subTransportTypeOptions
                    );
                  if (parentTransportType) {
                    setValue('transportType', parentTransportType, {
                      shouldValidate: true,
                    });
                  }
                }}
                value={
                  transportType === TransportType.AUTO
                    ? TransportType.AUTO
                    : field.value || undefined
                }
              >
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent />
              </Select>
            )}
          />
        ) : (
          <Controller
            name='transportType'
            control={control}
            rules={{ required: 'Required' }}
            render={({ field }: { field: ControllerRenderProps<any> }) => (
              <Select onValueChange={field.onChange} value={field.value}>
                <SelectTrigger className='w-full mt-1'>
                  <SelectValue placeholder='Choose' />
                </SelectTrigger>
                <SelectContent>
                  {transportTypeOptions.map((option: TransportTypeOption) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            )}
          />
        )}
        <ErrorMessage
          errors={errors}
          name={
            useGranularEquipmentSelect ? 'subTransportType' : 'transportType'
          }
          render={({ message }: { message: string }) => (
            <Typography variant='body-xs' className='text-error-500'>
              {message}
            </Typography>
          )}
        />
      </div>

      {transportType === TransportType.AUTO && (
        <div className='w-full min-w-0 col-span-1'>
          <AutoTransportFields formMethods={formMethods} />
        </div>
      )}
    </Grid>
  );
};
